import { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InstituteLogo } from "@/components/InstituteLogo";
import { INSTITUTE_NAME } from "@/lib/branding";
import { SlideRenderer } from "./slides/SlideRenderer";
import type { SlideData } from "@/types/lesson";

interface SlideAreaProps {
  slide: SlideData | null;
  slideIndex: number;
  isChildTheme: boolean;
  onQuizCorrect?: () => void;
  isQuizAnswered?: (i: number) => boolean;
}

export function SlideArea({
  slide,
  slideIndex,
  isChildTheme,
  onQuizCorrect,
  isQuizAnswered,
}: SlideAreaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!containerRef.current || !slide) return;
    const el = containerRef.current;
    const check = () => {
      const child = el.firstElementChild as HTMLElement | null;
      if (!child) return;
      const overflow = child.scrollHeight > el.clientHeight || child.scrollWidth > el.clientWidth;
      if (overflow && zoom > 0.3) {
        setZoom((z) => Math.max(0.3, z - 0.1));
      }
    };
    check();
    const obs = new ResizeObserver(check);
    obs.observe(el);
    return () => obs.disconnect();
  }, [slide?.type, slide?.title, zoom]);

  useEffect(() => {
    setZoom(1);
  }, [slideIndex]);

  if (!slide) return <div className="flex-1 min-h-0" />;

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-2"
    >
      <motion.div
        className="w-full h-full max-w-4xl mx-auto origin-center flex flex-col min-h-0"
        style={{ zoom }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 mb-2 pb-2 border-b border-slate-200/90 bg-white/60 backdrop-blur-sm rounded-t-lg px-2 sm:px-3 py-1.5">
          <InstituteLogo height={isChildTheme ? 42 : 34} className="shrink-0" />
          <div className="min-w-0 flex flex-col">
            <span className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-[0.12em] leading-tight">
              {INSTITUTE_NAME}
            </span>
            <span className="text-[11px] text-slate-400 hidden sm:block">Interactive lesson</span>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <AnimatePresence mode="wait">
          <SlideRenderer
            key={slideIndex}
            slide={slide}
            slideIndex={slideIndex}
            isChildTheme={isChildTheme}
            onQuizCorrect={onQuizCorrect}
            isQuizAnswered={isQuizAnswered?.(slideIndex)}
          />
        </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
